import { lazy, useEffect, useState } from 'react';
import { createBrowserRouter, createHashRouter, Link, Navigate, useRouteError } from 'react-router-dom';
import { REGISTERED_MODULE_UIS } from './modules';
import { LazyPage } from './components/layout/LazyPage';
import { ProtectedRoute } from '@/components/layout/ProtectedRoute';
import { AppLayout } from '@/components/layout/AppLayout';
import { LandingRedirect } from '@/components/layout/LandingRedirect';
import { isChunkLoadError, reloadOnceForChunkError } from '@/lib/chunk-error';
// Eager: the login screen and the dashboard are the first screens shown,
// so they skip a chunk round-trip. Every other page below is lazy().
import { LoginPage } from '@/pages/auth/LoginPage';
import { DashboardPage } from '@/pages/dashboard/DashboardPage';
import { LEGACY_USER_GUIDE_PATHS, useTranslation } from '@/lib/i18n';
import { SETTINGS_PERMISSIONS } from '@/pages/admin/settings/sections';
import { BILLING_ROUTES } from '@efacture/web-billing';

/** The route object of a billing mount: its page under its gate, as BILLING_ROUTES declares it. */
function billingRoute(path: string) {
  const route = BILLING_ROUTES.find((entry) => entry.path === path);
  if (!route) throw new Error(`Route de facturation inconnue : ${path}`);
  const { Component, permission, anyPermission, props } = route;
  return {
    path,
    element: (
      <ProtectedRoute {...(permission !== undefined && { permission })} {...(anyPermission !== undefined && { anyPermission })}>
        <LazyPage>
          <Component {...props} />
        </LazyPage>
      </ProtectedRoute>
    ),
  };
}

// ─────────────────────────────────────────────────────────────
// Lazy-loaded pages (chargement différé — code-splitting)
// ─────────────────────────────────────────────────────────────

const InvoiceFromFilePage = lazy(() => import('@/pages/invoices/InvoiceFromFilePage'));
const ClientListPage = lazy(() => import('@/pages/clients/ClientListPage'));
const ClientCreatePage = lazy(() => import('@/pages/clients/ClientCreatePage'));
const ClientEditPage = lazy(() => import('@/pages/clients/ClientEditPage'));
const ClientDetailPage = lazy(() => import('@/pages/clients/ClientDetailPage'));
const ArticleListPage = lazy(() => import('@/pages/articles/ArticleListPage'));
const ArticleCreatePage = lazy(() => import('@/pages/articles/ArticleCreatePage'));
const ArticleDetailPage = lazy(() => import('@/pages/articles/ArticleDetailPage'));
const ArticleEditPage = lazy(() => import('@/pages/articles/ArticleEditPage'));
const StockOverviewPage = lazy(() => import('@/pages/stock/StockOverviewPage'));
const StockReceivePage = lazy(() => import('@/pages/stock/StockReceivePage'));
const StockAdjustPage = lazy(() => import('@/pages/stock/StockAdjustPage'));
const StockMovementsPage = lazy(() => import('@/pages/stock/StockMovementsPage'));
const StockLocationsPage = lazy(() => import('@/pages/stock/StockLocationsPage'));
const InventoryListPage = lazy(() => import('@/pages/stock/InventoryListPage'));
const InventoryDetailPage = lazy(() => import('@/pages/stock/InventoryDetailPage'));
const UsersPage = lazy(() => import('@/pages/admin/UsersPage'));
const UserFormPage = lazy(() => import('@/pages/admin/UserFormPage'));
const RolesPage = lazy(() => import('@/pages/admin/RolesPage'));
const RoleEditorPage = lazy(() => import('@/pages/admin/RoleEditorPage'));
const AuditLogPage = lazy(() => import('@/pages/admin/AuditLogPage'));
const WorkflowConfigPage = lazy(() => import('@/pages/admin/WorkflowConfigPage'));
const ERPConnectionConfigPage = lazy(() => import('@/pages/admin/ERPConnectionConfigPage'));
const PispiEventsPage = lazy(() => import('@/pages/admin/PispiEventsPage'));
const NodeHealthPage = lazy(() => import('@/pages/admin/NodeHealthPage'));
const EstablishmentsPage = lazy(() => import('@/pages/admin/EstablishmentsPage'));
const SettingsLayout = lazy(() => import('@/pages/admin/settings/SettingsLayout'));
const SettingsIndexRedirect = lazy(() => import('@/pages/admin/settings/SettingsIndexRedirect'));
const EntrepriseSection = lazy(() => import('@/pages/admin/settings/EntrepriseSection'));
const ModulesSection = lazy(() => import('@/pages/admin/settings/ModulesSection'));
const FiscaliteSection = lazy(() => import('@/pages/admin/settings/FiscaliteSection'));
const FacturationSection = lazy(() => import('@/pages/admin/settings/FacturationSection'));
const FideliteSection = lazy(() => import('@/pages/admin/settings/FideliteSection'));
const CaisseSection = lazy(() => import('@/pages/admin/settings/CaisseSection'));
const ModulesMenusSection = lazy(() => import('@/pages/admin/settings/ModulesMenusSection'));
const TableauDeBordSection = lazy(() => import('@/pages/admin/settings/TableauDeBordSection'));
const ComptesBancairesSection = lazy(() => import('@/pages/admin/settings/ComptesBancairesSection'));
const ParametrageDgiSection = lazy(() => import('@/pages/admin/settings/ParametrageDgiSection'));
const NotificationsSection = lazy(() => import('@/pages/admin/settings/NotificationsSection'));
const PispiSection = lazy(() => import('@/pages/admin/settings/PispiSection'));
const McfAgentsSection = lazy(() => import('@/pages/admin/settings/McfAgentsSection'));
const McfDevicesSection = lazy(() => import('@/pages/admin/settings/McfDevicesSection'));
const McfOutagesSection = lazy(() => import('@/pages/admin/settings/McfOutagesSection'));
const MiseEnServiceSection = lazy(() => import('@/pages/admin/settings/MiseEnServiceSection'));
const ApparenceSection = lazy(() => import('@/pages/admin/settings/ApparenceSection'));
const AssistantCommandsSection = lazy(() => import('@/pages/admin/settings/AssistantCommandsSection'));
const ProfilePage = lazy(() => import('@/pages/profile/ProfilePage'));
const AboutPage = lazy(() => import('@/pages/about/AboutPage'));
const SalesReportPage = lazy(() => import('@/pages/reports/SalesReportPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPasswordPage'));

// ─────────────────────────────────────────────────────────────
// Comptabilité (chargement différé — code-splitting)
// ─────────────────────────────────────────────────────────────

const AccountingDashboardPage = lazy(() => import('@/pages/accounting/AccountingDashboardPage'));
const ChartOfAccountsPage = lazy(() => import('@/pages/accounting/ChartOfAccountsPage'));
const AgedBalancePage = lazy(() => import('@/pages/accounting/AgedBalancePage'));
const JournalEntriesPage = lazy(() => import('@/pages/accounting/JournalEntriesPage'));
const JournalEntryFormPage = lazy(() => import('@/pages/accounting/JournalEntryFormPage'));
const JournalEntryDetailPage = lazy(() => import('@/pages/accounting/JournalEntryDetailPage'));
const AccountingPeriodsPage = lazy(() => import('@/pages/accounting/AccountingPeriodsPage'));
const GeneralLedgerPage = lazy(() => import('@/pages/accounting/GeneralLedgerPage'));
const JournalReportPage = lazy(() => import('@/pages/accounting/JournalReportPage'));
const AuxiliariesPage = lazy(() => import('@/pages/accounting/AuxiliariesPage'));
const AuxiliaryDetailPage = lazy(() => import('@/pages/accounting/AuxiliaryDetailPage'));
const AuxiliaryBalancePage = lazy(() => import('@/pages/accounting/AuxiliaryBalancePage'));
const TrialBalancePage = lazy(() => import('@/pages/accounting/TrialBalancePage'));
const VatDeclarationPage = lazy(() => import('@/pages/accounting/VatDeclarationPage'));
const FinancialStatementsPage = lazy(() => import('@/pages/accounting/FinancialStatementsPage'));
const SycebnlStatementsPage = lazy(() => import('@/pages/accounting/SycebnlStatementsPage'));
const DonorRegisterPage = lazy(() => import('@/pages/accounting/DonorRegisterPage'));
const FundsPage = lazy(() => import('@/pages/accounting/FundsPage'));
// Verticale ONG palier A (Task 4) — référentiel financement
const FundingPage = lazy(() => import('@/pages/funding/FundingPage'));
// Moteur budgétaire (verticale ONG palier B1, Task 8) : enveloppes,
// engagements/liquidations, vue d'exécution.
const BudgetEnvelopesPage = lazy(() => import('@/pages/budget/BudgetEnvelopesPage'));
const BudgetCommitmentsPage = lazy(() => import('@/pages/budget/BudgetCommitmentsPage'));
const BudgetExecutionPage = lazy(() => import('@/pages/budget/BudgetExecutionPage'));
// ★ Mode public (palier B2, Task 7) — la file du contrôleur financier et
// l'écran ordonnateur/comptable. Gardées par `budget:read` comme les
// autres : ce sont les MUTATIONS qui portent les permissions séparées
// (`budget:visa`, `budget:passer-outre`, `budget:pay`), et un lecteur a le
// droit de voir la file qu'il ne peut pas actionner.
const BudgetVisaPage = lazy(() => import('@/pages/budget/BudgetVisaPage'));
const BudgetOrdonnancementPage = lazy(
  () => import('@/pages/budget/BudgetOrdonnancementPage')
);
// Reporting bailleur (verticale ONG palier C, Task 6) : RSF/IFR, SOE,
// DRF, comptes désignés.
const DonorReportsPage = lazy(() => import('@/pages/reporting/DonorReportsPage'));
const LiasseEsintaxPage = lazy(() => import('@/pages/accounting/LiasseEsintaxPage'));
const LetteringPage = lazy(() => import('@/pages/accounting/LetteringPage'));
const ReconciliationPage = lazy(() => import('@/pages/accounting/ReconciliationPage'));
const SupportingDocumentsPage = lazy(
  () => import('@/pages/accounting/SupportingDocumentsPage')
);
const DocumentProposalPage = lazy(() => import('@/pages/accounting/DocumentProposalPage'));
const BalanceProposalPage = lazy(() => import('@/pages/accounting/BalanceProposalPage'));
const AccountingSettingsPage = lazy(() => import('@/pages/accounting/AccountingSettingsPage'));
const ClosingPage = lazy(() => import('@/pages/accounting/ClosingPage'));
const SuppliersPage = lazy(() => import('@/pages/accounting/SuppliersPage'));
const SupplierInvoicesPage = lazy(() => import('@/pages/accounting/SupplierInvoicesPage'));
const SupplierInvoiceFormPage = lazy(() => import('@/pages/accounting/SupplierInvoiceFormPage'));
const SupplierInvoiceDetailPage = lazy(() => import('@/pages/accounting/SupplierInvoiceDetailPage'));
const AssetsPage = lazy(() => import('@/pages/accounting/AssetsPage'));
const AssetFormPage = lazy(() => import('@/pages/accounting/AssetFormPage'));
const AssetDetailPage = lazy(() => import('@/pages/accounting/AssetDetailPage'));
const DepreciationsPage = lazy(() => import('@/pages/accounting/DepreciationsPage'));
const CostCentersPage = lazy(() => import('@/pages/accounting/CostCentersPage'));
const AnalyticAxesPage = lazy(() => import('@/pages/accounting/AnalyticAxesPage'));
const BudgetsPage = lazy(() => import('@/pages/accounting/BudgetsPage'));
const BudgetFormPage = lazy(() => import('@/pages/accounting/BudgetFormPage'));
const BudgetVsActualPage = lazy(() => import('@/pages/accounting/BudgetVsActualPage'));
const ExchangeRatesPage = lazy(() => import('@/pages/accounting/ExchangeRatesPage'));
const ReportDefinitionsPage = lazy(() => import('@/pages/accounting/ReportDefinitionsPage'));
const ReportDefinitionFormPage = lazy(() => import('@/pages/accounting/ReportDefinitionFormPage'));
const ReportRunPage = lazy(() => import('@/pages/accounting/ReportRunPage'));

// ─────────────────────────────────────────────────────────────
// Suite ERP — Achats, RH, CRM, Projets (chargement différé)
// ─────────────────────────────────────────────────────────────

const PurchaseOrdersPage = lazy(() => import('@/pages/purchasing/PurchaseOrdersPage'));
const PurchaseOrderFormPage = lazy(() => import('@/pages/purchasing/PurchaseOrderFormPage'));
const PurchaseOrderDetailPage = lazy(() => import('@/pages/purchasing/PurchaseOrderDetailPage'));
const EmployeesPage = lazy(() => import('@/pages/hr/EmployeesPage'));
const EmployeeFormPage = lazy(() => import('@/pages/hr/EmployeeFormPage'));
const LeavesPage = lazy(() => import('@/pages/hr/LeavesPage'));
const PayrollRunsPage = lazy(() => import('@/pages/hr/PayrollRunsPage'));
const PayrollRunDetailPage = lazy(() => import('@/pages/hr/PayrollRunDetailPage'));
const PayrollParametersPage = lazy(() => import('@/pages/hr/PayrollParametersPage'));
const LeadsPage = lazy(() => import('@/pages/crm/LeadsPage'));
const LeadDetailPage = lazy(() => import('@/pages/crm/LeadDetailPage'));
const OpportunitiesPage = lazy(() => import('@/pages/crm/OpportunitiesPage'));
const CrmActivitiesPage = lazy(() => import('@/pages/crm/CrmActivitiesPage'));
const ProjectsPage = lazy(() => import('@/pages/projects/ProjectsPage'));
const ProjectFormPage = lazy(() => import('@/pages/projects/ProjectFormPage'));
const ProjectDetailPage = lazy(() => import('@/pages/projects/ProjectDetailPage'));

// ─────────────────────────────────────────────────────────────
// Machinerie commerciale SaaS + onboarding (P0.3 / P0.4)
// ─────────────────────────────────────────────────────────────

const SignupPage = lazy(() => import('@/pages/public/SignupPage'));
const FirstAdminPage = lazy(() => import('@/pages/public/FirstAdminPage'));
const VerifyEmailPage = lazy(() => import('@/pages/public/VerifyEmailPage'));
const PayerEnLignePage = lazy(() => import('@/pages/public/PayerEnLignePage'));
const LegacyGuidePage = lazy(() => import('@/pages/public/LegacyGuidePage'));
const StatusPage = lazy(() => import('@/pages/billing/StatusPage'));
const BillingPage = lazy(() => import('@/pages/billing/BillingPage'));
const OnboardingPage = lazy(() => import('@/pages/onboarding/OnboardingPage'));
const ImportsPage = lazy(() => import('@/pages/onboarding/ImportsPage'));

// ─────────────────────────────────────────────────────────────
// Ventes P1 — devis, bons de livraison, récurrentes, relances
// ─────────────────────────────────────────────────────────────

const QuotesPage = lazy(() => import('@/pages/sales/QuotesPage'));
const QuoteFormPage = lazy(() => import('@/pages/sales/QuoteFormPage'));
const QuoteDetailPage = lazy(() => import('@/pages/sales/QuoteDetailPage'));
const DeliveryNotesPage = lazy(() => import('@/pages/sales/DeliveryNotesPage'));
const DeliveryNoteFormPage = lazy(() => import('@/pages/sales/DeliveryNoteFormPage'));
const RecurringInvoicesPage = lazy(() => import('@/pages/sales/RecurringInvoicesPage'));
const RemindersSettingsPage = lazy(() => import('@/pages/sales/RemindersSettingsPage'));

// ─────────────────────────────────────────────────────────────
// Déclarations & calendrier fiscal (P1)
// ─────────────────────────────────────────────────────────────

const DeclarationsPage = lazy(() => import('@/pages/declarations/DeclarationsPage'));
const FiscalCalendarPage = lazy(() => import('@/pages/declarations/FiscalCalendarPage'));

// ─────────────────────────────────────────────────────────────
// Comptabilité simplifiée (SMT) + fiches d'importation (landed cost)
// ─────────────────────────────────────────────────────────────

const SmtPage = lazy(() => import('@/pages/smt/SmtPage'));
const ImportCostSheetsPage = lazy(() => import('@/pages/purchasing/ImportCostSheetsPage'));
const ImportCostSheetDetailPage = lazy(() => import('@/pages/purchasing/ImportCostSheetDetailPage'));

// ─────────────────────────────────────────────────────────────
// Portail cabinet comptable (P1.4) + analyses/BI + webhooks (P2)
// ─────────────────────────────────────────────────────────────

const FirmPage = lazy(() => import('@/pages/firm/FirmPage'));
const FirmDashboardPage = lazy(() => import('@/pages/firm/FirmDashboardPage'));
const CabinetSettingsPage = lazy(() => import('@/pages/firm/CabinetSettingsPage'));
const AnalyticsPage = lazy(() => import('@/pages/analytics/AnalyticsPage'));
const ReportsHubLayout = lazy(() => import('@/pages/reports/ReportsHubLayout'));
const WebhooksPage = lazy(() => import('@/pages/webhooks/WebhooksPage'));



function NotFoundPage() {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50">
      <div className="text-center">
        <h1 className="text-8xl font-bold text-slate-200">404</h1>
        <h2 className="mt-4 text-2xl font-semibold text-slate-900">
          {t('error.notFound.title')}
        </h2>
        <p className="mt-2 text-slate-500">{t('error.notFound.description')}</p>
        <a
          href="/"
          className="mt-6 inline-block rounded-lg bg-brand-lagune-700 px-6 py-2.5 text-sm font-medium text-white hover:bg-brand-lagune-800 transition-colors"
        >
          {t('common.home')}
        </a>
      </div>
    </div>
  );
}

/**
 * `errorElement` of every top-level route: covers the public pages outside
 * AppLayout and a crash in AppLayout's own chrome (page crashes inside it
 * are caught by PageErrorBoundary). A chunk-load error from a stale deploy
 * reloads the page (chunk-error.ts).
 */
function RouteErrorPage() {
  const error = useRouteError();
  const chunkError = isChunkLoadError(error);
  const [reloading, setReloading] = useState<boolean | null>(null);

  useEffect(() => {
    if (chunkError) {
      setReloading(reloadOnceForChunkError());
    } else {
      console.error('Erreur de route :', error);
    }
  }, [error, chunkError]);

  if (chunkError && reloading !== false) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 text-sm text-slate-500">
        Mise à jour de l'application…
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50">
      <div className="text-center">
        <h1 className="text-2xl font-semibold text-slate-900">
          Une erreur est survenue
        </h1>
        <p className="mt-2 text-slate-500">
          Rechargez la page. Si le problème persiste, contactez le support.
        </p>
        <div className="mt-6 flex justify-center gap-3">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-lg border border-slate-300 px-6 py-2.5 text-sm font-medium hover:bg-slate-50"
          >
            Réessayer
          </button>
          <Link
            to="/"
            className="inline-block rounded-lg bg-brand-lagune-700 px-6 py-2.5 text-sm font-medium text-white hover:bg-brand-lagune-800 transition-colors"
          >
            Accueil
          </Link>
        </div>
      </div>
    </div>
  );
}

function ForbiddenPage() {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50">
      <div className="text-center">
        <h1 className="text-8xl font-bold text-red-200">403</h1>
        <h2 className="mt-4 text-2xl font-semibold text-slate-900">
          {t('error.forbidden.title')}
        </h2>
        <p className="mt-2 text-slate-500">{t('error.forbidden.description')}</p>
        <a
          href="/"
          className="mt-6 inline-block rounded-lg bg-brand-lagune-700 px-6 py-2.5 text-sm font-medium text-white hover:bg-brand-lagune-800 transition-colors"
        >
          {t('common.home')}
        </a>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Protected Layout with AppLayout
// ─────────────────────────────────────────────────────────────

function ProtectedLayout() {
  return (
    <ProtectedRoute>
      <AppLayout />
    </ProtectedRoute>
  );
}

// ─────────────────────────────────────────────────────────────
// Router Configuration
// ─────────────────────────────────────────────────────────────

// Sous Electron, l'app est chargée via loadFile → URL file:///…/index.html :
// le chemin initial ne matche aucune route (404 immédiat) et l'historique
// BrowserRouter ne peut pas naviguer sur file://. Le hash-router est le mode
// standard pour un renderer chargé en fichier ; sur http(s) (SaaS/PWA), le
// BrowserRouter reste inchangé.
const createRouter =
  typeof window !== 'undefined' && window.location.protocol === 'file:'
    ? createHashRouter
    : createBrowserRouter;

/** Every route the sector modules bring, in registry order. */
const MODULE_ROUTES = REGISTERED_MODULE_UIS.flatMap((ui) => ui.routes);

export const router = createRouter([
  // ─────────────────────────────────────────────────────────
  // Public Routes
  // ─────────────────────────────────────────────────────────
  {
    path: '/login',
    element: <LoginPage />,
    errorElement: <RouteErrorPage />,
  },
  {
    path: '/forgot-password',
    element: <LazyPage><ForgotPasswordPage /></LazyPage>,
    errorElement: <RouteErrorPage />,
  },
  {
    path: '/reset-password',
    element: <LazyPage><ResetPasswordPage /></LazyPage>,
    errorElement: <RouteErrorPage />,
  },
  // Self-serve signup + email verification + platform status — public,
  // outside the auth guard, same as /login.
  {
    path: '/inscription',
    element: (
      <LazyPage>
        <SignupPage />
      </LazyPage>
    ),
    errorElement: <RouteErrorPage />,
  },
  // First administrator of a site node attached to a cloud — public, one-time code.
  {
    path: '/premiere-connexion',
    element: (
      <LazyPage>
        <FirstAdminPage />
      </LazyPage>
    ),
    errorElement: <RouteErrorPage />,
  },
  {
    path: '/verification-email',
    element: (
      <LazyPage>
        <VerifyEmailPage />
      </LazyPage>
    ),
    errorElement: <RouteErrorPage />,
  },
  {
    path: '/status',
    element: (
      <LazyPage>
        <StatusPage />
      </LazyPage>
    ),
    errorElement: <RouteErrorPage />,
  },
  // Public « Payer en ligne » page, opened by a supplier's customer from a
  // payment link: no session, outside the auth guard.
  {
    path: '/payer/:token',
    element: (
      <LazyPage>
        <PayerEnLignePage />
      </LazyPage>
    ),
    errorElement: <RouteErrorPage />,
  },
  // Earlier names of the user guide: public, so a logged-out bookmark still
  // reaches the current file.
  ...LEGACY_USER_GUIDE_PATHS.map((path) => ({
    path,
    element: (
      <LazyPage>
        <LegacyGuidePage />
      </LazyPage>
    ),
    errorElement: <RouteErrorPage />,
  })),

  // ─────────────────────────────────────────────────────────
  // Error Pages
  // ─────────────────────────────────────────────────────────
  {
    path: '/403',
    element: <ForbiddenPage />,
    errorElement: <RouteErrorPage />,
  },
  {
    path: '/404',
    element: <NotFoundPage />,
    errorElement: <RouteErrorPage />,
  },

  // ─────────────────────────────────────────────────────────
  // Protected Routes with AppLayout
  // ─────────────────────────────────────────────────────────
  {
    path: '/',
    element: <ProtectedLayout />,
    // A crash in ProtectedLayout/AppLayout itself; page crashes are caught
    // by PageErrorBoundary around the Outlet (AppLayout.tsx).
    errorElement: <RouteErrorPage />,
    children: [
      // Root — resolved from the user's role and the tenant's sector,
      // falling back to /dashboard (see LandingRedirect).
      {
        index: true,
        element: <LandingRedirect />,
      },

      // Dashboard - accessible by all authenticated users
      {
        path: 'dashboard',
        element: <DashboardPage />,
      },

      // Profile - accessible by all authenticated users
      {
        path: 'profile',
        element: <LazyPage><ProfilePage /></LazyPage>,
      },

      // « À propos » - accessible by all authenticated users
      {
        path: 'a-propos',
        element: <LazyPage><AboutPage /></LazyPage>,
      },

      // Assistant d'onboarding (P0.4) — tous les utilisateurs authentifiés
      {
        path: 'bienvenue',
        element: (
          <LazyPage>
            <OnboardingPage />
          </LazyPage>
        ),
      },

      // Abonnement SaaS (P0.3)
      {
        path: 'parametres/abonnement',
        element: (
          <ProtectedRoute permission="billing:read">
            <LazyPage>
              <BillingPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },

      // Migration imports: the page shows the tabs the user's permissions
      // open (IMPORT_TAB_PERMISSIONS in ImportsPage).
      {
        path: 'parametres/imports',
        element: (
          <LazyPage>
            <ImportsPage />
          </LazyPage>
        ),
      },

      // Invoices
      billingRoute('invoices'),
      billingRoute('invoices/external-sales'),
      {
        path: 'invoices/from-file',
        element: (
          <ProtectedRoute permission="invoices:create">
            <LazyPage>
              <InvoiceFromFilePage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      billingRoute('invoices/new'),
      billingRoute('invoices/verification'),
      billingRoute('invoices/signatures-en-doute'),
      billingRoute('invoices/:id'),

      // POS (Point of Sale)
      billingRoute('pos'),

      // Clients
      {
        path: 'clients',
        element: (
          <ProtectedRoute permission="clients:read">
            <LazyPage>
              <ClientListPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'clients/new',
        element: (
          <ProtectedRoute permission="clients:create">
            <LazyPage>
              <ClientCreatePage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'clients/:id',
        element: (
          <ProtectedRoute permission="clients:read">
            <LazyPage>
              <ClientDetailPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'clients/:id/edit',
        element: (
          <ProtectedRoute permission="clients:update">
            <LazyPage>
              <ClientEditPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },

      // Articles
      {
        path: 'articles',
        element: (
          <ProtectedRoute permission="articles:read">
            <LazyPage>
              <ArticleListPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'articles/new',
        element: (
          <ProtectedRoute permission="articles:create">
            <LazyPage>
              <ArticleCreatePage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'articles/:id',
        element: (
          <ProtectedRoute permission="articles:read">
            <LazyPage>
              <ArticleDetailPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'articles/:id/edit',
        element: (
          <ProtectedRoute permission="articles:update">
            <LazyPage>
              <ArticleEditPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },

      // Stock
      {
        path: 'stock',
        element: (
          <ProtectedRoute permission="stock:read">
            <LazyPage>
              <StockOverviewPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'stock/receive',
        element: (
          <ProtectedRoute permission="stock:receive">
            <LazyPage>
              <StockReceivePage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'stock/adjust',
        element: (
          <ProtectedRoute permission="stock:adjust">
            <LazyPage>
              <StockAdjustPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'stock/movements',
        element: (
          <ProtectedRoute permission="stock:movements">
            <LazyPage>
              <StockMovementsPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'stock/depots',
        element: (
          <ProtectedRoute permission="stock:adjust">
            <LazyPage>
              <StockLocationsPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'stock/inventaires',
        element: (
          <ProtectedRoute anyPermission={['stock:inventory', 'stock:inventory:count']}>
            <LazyPage>
              <InventoryListPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'stock/inventaires/:id',
        element: (
          <ProtectedRoute anyPermission={['stock:inventory', 'stock:inventory:count']}>
            <LazyPage>
              <InventoryDetailPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },

      // Hub « Rapports & analyses » (réorg menu 2026-07-18, prop. C) — les
      // anciennes routes /reports, /analyses et /accounting/rapports
      // redirigent vers les onglets ; les sous-rapports (/reports/z, etc.)
      // gardent leurs routes.
      {
        path: 'rapports',
        element: (
          <LazyPage>
            <ReportsHubLayout />
          </LazyPage>
        ),
        children: [
          { index: true, element: <Navigate to="general" replace /> },
          billingRoute('general'),
          {
            path: 'analyses',
            element: (
              <ProtectedRoute permission="dashboard:view">
                <LazyPage>
                  <AnalyticsPage />
                </LazyPage>
              </ProtectedRoute>
            ),
          },
          {
            path: 'personnalises',
            element: (
              <ProtectedRoute permission="accounting:read">
                <LazyPage>
                  <ReportDefinitionsPage />
                </LazyPage>
              </ProtectedRoute>
            ),
          },
        ],
      },
      billingRoute('rapports-fiscaux'),
      {
        path: 'reports',
        element: <Navigate to="/rapports/general" replace />,
      },
      billingRoute('reports/x-daily'),
      billingRoute('reports/z'),
      {
        path: 'reports/sales',
        element: (
          <ProtectedRoute permission="reports:sales">
            <LazyPage>
              <SalesReportPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      billingRoute('reports/articles'),
      billingRoute('reports/fec'),
      billingRoute('reports/factures-a-regulariser'),
      billingRoute('reports/documents-a-regulariser'),
      billingRoute('reports/reeditions-originaux'),

      // Comptabilité (SYSCOHADA) — pages en chargement différé
      {
        path: 'accounting',
        element: (
          <ProtectedRoute permission="accounting:read">
            <LazyPage>
              <AccountingDashboardPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'accounting/plan-comptable',
        element: (
          <ProtectedRoute permission="accounting:read">
            <LazyPage>
              <ChartOfAccountsPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'accounting/ecritures',
        element: (
          <ProtectedRoute permission="accounting:read">
            <LazyPage>
              <JournalEntriesPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'accounting/ecritures/nouvelle',
        element: (
          <ProtectedRoute permission="accounting:write">
            <LazyPage>
              <JournalEntryFormPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'accounting/ecritures/:id',
        element: (
          <ProtectedRoute permission="accounting:read">
            <LazyPage>
              <JournalEntryDetailPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'accounting/ecritures/:id/modifier',
        element: (
          <ProtectedRoute permission="accounting:write">
            <LazyPage>
              <JournalEntryFormPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'accounting/periodes',
        element: (
          <ProtectedRoute permission="accounting:read">
            <LazyPage>
              <AccountingPeriodsPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'accounting/journal',
        element: (
          <ProtectedRoute permission="accounting:read">
            <LazyPage>
              <JournalReportPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'accounting/grand-livre',
        element: (
          <ProtectedRoute permission="accounting:read">
            <LazyPage>
              <GeneralLedgerPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'accounting/balance',
        element: (
          <ProtectedRoute permission="accounting:read">
            <LazyPage>
              <TrialBalancePage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        /**
         * The trial balance opened on the analytic axis, under a path of its
         * own: the sidebar matches `location.pathname` against `item.href`
         * (exactly or by prefix, Sidebar.tsx `NavItemComponent`), and a
         * pathname never carries the query string, so a `?analytique=1`
         * entry would never light up.
         */
        path: 'accounting/balance-analytique',
        element: (
          <ProtectedRoute permission="accounting:read">
            <LazyPage>
              <TrialBalancePage analyticFirst />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'accounting/balance-agee',
        element: (
          <ProtectedRoute permission="accounting:read">
            <LazyPage>
              <AgedBalancePage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      // Comptes tiers (Task 4) — répertoire, fiche, balance auxiliaire
      {
        path: 'accounting/tiers',
        element: (
          <ProtectedRoute permission="accounting:read">
            <LazyPage>
              <AuxiliariesPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'accounting/tiers/:id',
        element: (
          <ProtectedRoute permission="accounting:read">
            <LazyPage>
              <AuxiliaryDetailPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'accounting/balance-auxiliaire',
        element: (
          <ProtectedRoute permission="accounting:read">
            <LazyPage>
              <AuxiliaryBalancePage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'accounting/tva',
        element: (
          <ProtectedRoute permission="accounting:read">
            <LazyPage>
              <VatDeclarationPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'accounting/etats',
        element: (
          <ProtectedRoute permission="accounting:read">
            <LazyPage>
              <FinancialStatementsPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'accounting/etats-sycebnl',
        element: (
          <ProtectedRoute permission="accounting:read">
            <LazyPage>
              <SycebnlStatementsPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'accounting/registre-donateurs',
        element: (
          <ProtectedRoute permission="accounting:read">
            <LazyPage>
              <DonorRegisterPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'accounting/fonds-affectes',
        element: (
          <ProtectedRoute permission="accounting:read">
            <LazyPage>
              <FundsPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'financements',
        element: (
          <ProtectedRoute permission="funding:read">
            <LazyPage>
              <FundingPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'rapports-bailleur',
        element: (
          <ProtectedRoute permission="reporting:read">
            <LazyPage>
              <DonorReportsPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'budget/enveloppes',
        element: (
          <ProtectedRoute permission="budget:read">
            <LazyPage>
              <BudgetEnvelopesPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'budget/engagements',
        element: (
          <ProtectedRoute permission="budget:read">
            <LazyPage>
              <BudgetCommitmentsPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'budget/visas',
        element: (
          <ProtectedRoute permission="budget:read">
            <LazyPage>
              <BudgetVisaPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'budget/ordonnancement',
        element: (
          <ProtectedRoute permission="budget:read">
            <LazyPage>
              <BudgetOrdonnancementPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'budget/execution',
        element: (
          <ProtectedRoute permission="budget:read">
            <LazyPage>
              <BudgetExecutionPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'accounting/liasse-esintax',
        element: (
          <ProtectedRoute permission="accounting:read">
            <LazyPage>
              <LiasseEsintaxPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'accounting/lettrage',
        element: (
          <ProtectedRoute permission="accounting:read">
            <LazyPage>
              <LetteringPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'accounting/rapprochement',
        element: (
          <ProtectedRoute permission="accounting:read">
            <LazyPage>
              <ReconciliationPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        // Pièces justificatives — capture (phase 1). Lecture pour consulter
        // et télécharger ; le téléversement et l'archivage exigent
        // accounting:write, appliqué côté API et côté écran.
        path: 'accounting/pieces-justificatives',
        element: (
          <ProtectedRoute permission="accounting:read">
            <LazyPage>
              <SupportingDocumentsPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'accounting/pieces-justificatives/:id/proposition',
        element: (
          <ProtectedRoute permission="accounting:read">
            <LazyPage>
              <DocumentProposalPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        // Une balance ne porte pas d'écriture : sa revue est la confrontation
        // au grand livre, pas le formulaire de mapping des colonnes.
        path: 'accounting/pieces-justificatives/:id/balance',
        element: (
          <ProtectedRoute permission="accounting:read">
            <LazyPage>
              <BalanceProposalPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'accounting/cloture',
        element: (
          <ProtectedRoute permission="accounting:close">
            <LazyPage>
              <ClosingPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'accounting/parametres',
        element: (
          <ProtectedRoute permission="accounting:settings">
            <LazyPage>
              <AccountingSettingsPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },

      // Comptabilité Phase 2.x — Fournisseurs & Immobilisations (vague 1)
      {
        path: 'accounting/fournisseurs',
        element: (
          <ProtectedRoute permission="suppliers:read">
            <LazyPage>
              <SuppliersPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'accounting/factures-fournisseurs',
        element: (
          <ProtectedRoute permission="suppliers:read">
            <LazyPage>
              <SupplierInvoicesPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'accounting/factures-fournisseurs/nouvelle',
        element: (
          <ProtectedRoute permission="suppliers:write">
            <LazyPage>
              <SupplierInvoiceFormPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'accounting/factures-fournisseurs/:id',
        element: (
          <ProtectedRoute permission="suppliers:read">
            <LazyPage>
              <SupplierInvoiceDetailPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'accounting/factures-fournisseurs/:id/modifier',
        element: (
          <ProtectedRoute permission="suppliers:write">
            <LazyPage>
              <SupplierInvoiceFormPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'accounting/immobilisations',
        element: (
          <ProtectedRoute permission="accounting:read">
            <LazyPage>
              <AssetsPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'accounting/immobilisations/nouvelle',
        element: (
          <ProtectedRoute permission="accounting:write">
            <LazyPage>
              <AssetFormPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        // AVANT `:id` — sans quoi « nouvelle » serait pris pour un
        // identifiant. `:id/modifier` ne peut pas entrer en collision,
        // mais l'ordre reste celui du fichier.
        path: 'accounting/immobilisations/:id/modifier',
        element: (
          <ProtectedRoute permission="accounting:write">
            <LazyPage>
              <AssetFormPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'accounting/immobilisations/:id',
        element: (
          <ProtectedRoute permission="accounting:read">
            <LazyPage>
              <AssetDetailPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'accounting/dotations',
        element: (
          <ProtectedRoute permission="accounting:read">
            <LazyPage>
              <DepreciationsPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },

      // Comptabilité Phase 2.x — Analytique & Budgets (vague 2)
      {
        path: 'accounting/centres-de-couts',
        element: (
          <ProtectedRoute permission="accounting:read">
            <LazyPage>
              <CostCentersPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      // Analytique multi-axe (verticale ONG palier A)
      {
        path: 'accounting/axes-analytiques',
        element: (
          <ProtectedRoute permission="accounting:read">
            <LazyPage>
              <AnalyticAxesPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'accounting/budgets',
        element: (
          <ProtectedRoute permission="accounting:read">
            <LazyPage>
              <BudgetsPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'accounting/budgets/nouveau',
        element: (
          <ProtectedRoute permission="accounting:write">
            <LazyPage>
              <BudgetFormPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'accounting/budgets/realise',
        element: (
          <ProtectedRoute permission="accounting:read">
            <LazyPage>
              <BudgetVsActualPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'accounting/budgets/:id',
        element: (
          <ProtectedRoute permission="accounting:write">
            <LazyPage>
              <BudgetFormPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'accounting/budgets/:id/modifier',
        element: (
          <ProtectedRoute permission="accounting:write">
            <LazyPage>
              <BudgetFormPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },

      // Comptabilité Phase 2.x — Multi-devise (vague 3)
      {
        path: 'accounting/taux-de-change',
        element: (
          <ProtectedRoute permission="accounting:settings">
            <LazyPage>
              <ExchangeRatesPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },

      // Comptabilité Phase 2.x — Rapports personnalisés (vague 6)
      {
        path: 'accounting/rapports',
        element: <Navigate to="/rapports/personnalises" replace />,
      },
      {
        path: 'accounting/rapports/nouveau',
        element: (
          <ProtectedRoute permission="accounting:write">
            <LazyPage>
              <ReportDefinitionFormPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'accounting/rapports/:id/modifier',
        element: (
          <ProtectedRoute permission="accounting:write">
            <LazyPage>
              <ReportDefinitionFormPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'accounting/rapports/:id/executer',
        element: (
          <ProtectedRoute permission="accounting:read">
            <LazyPage>
              <ReportRunPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },

      // Suite ERP — Achats (M2)
      {
        path: 'achats/bons-de-commande',
        element: (
          <ProtectedRoute permission="purchases:read">
            <LazyPage>
              <PurchaseOrdersPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'achats/bons-de-commande/nouveau',
        element: (
          <ProtectedRoute permission="purchases:write">
            <LazyPage>
              <PurchaseOrderFormPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'achats/bons-de-commande/:id',
        element: (
          <ProtectedRoute permission="purchases:read">
            <LazyPage>
              <PurchaseOrderDetailPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'achats/bons-de-commande/:id/modifier',
        element: (
          <ProtectedRoute permission="purchases:write">
            <LazyPage>
              <PurchaseOrderFormPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },

      // Suite ERP — RH & paie (M3/M4)
      {
        path: 'rh/employes',
        element: (
          <ProtectedRoute permission="hr:read">
            <LazyPage>
              <EmployeesPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'rh/employes/nouveau',
        element: (
          <ProtectedRoute permission="hr:write">
            <LazyPage>
              <EmployeeFormPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'rh/employes/:id/modifier',
        element: (
          <ProtectedRoute permission="hr:write">
            <LazyPage>
              <EmployeeFormPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'rh/conges',
        element: (
          <ProtectedRoute permission="hr:read">
            <LazyPage>
              <LeavesPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'rh/paie',
        element: (
          <ProtectedRoute permission="payroll:read">
            <LazyPage>
              <PayrollRunsPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'rh/paie/parametres',
        element: (
          <ProtectedRoute permission="payroll:read">
            <LazyPage>
              <PayrollParametersPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'rh/paie/:id',
        element: (
          <ProtectedRoute permission="payroll:read">
            <LazyPage>
              <PayrollRunDetailPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },

      // Suite ERP — CRM (M5)
      {
        path: 'crm/prospects',
        element: (
          <ProtectedRoute permission="crm:read">
            <LazyPage>
              <LeadsPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'crm/prospects/:id',
        element: (
          <ProtectedRoute permission="crm:read">
            <LazyPage>
              <LeadDetailPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'crm/opportunites',
        element: (
          <ProtectedRoute permission="crm:read">
            <LazyPage>
              <OpportunitiesPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'crm/activites',
        element: (
          <ProtectedRoute permission="crm:read">
            <LazyPage>
              <CrmActivitiesPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },

      // Suite ERP — Gestion de projets (M6)
      {
        path: 'projets',
        element: (
          <ProtectedRoute permission="projects:read">
            <LazyPage>
              <ProjectsPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'projets/nouveau',
        element: (
          <ProtectedRoute permission="projects:manage">
            <LazyPage>
              <ProjectFormPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'projets/:id',
        element: (
          <ProtectedRoute permission="projects:read">
            <LazyPage>
              <ProjectDetailPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'projets/:id/modifier',
        element: (
          <ProtectedRoute permission="projects:manage">
            <LazyPage>
              <ProjectFormPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },

      // ─────────────────────────────────────────────────────────
      // Ventes P1 — devis, bons de livraison, récurrentes, relances
      // ─────────────────────────────────────────────────────────
      {
        path: 'ventes/devis',
        element: (
          <ProtectedRoute permission="quotes:read">
            <LazyPage>
              <QuotesPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'ventes/devis/nouveau',
        element: (
          <ProtectedRoute permission="quotes:write">
            <LazyPage>
              <QuoteFormPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'ventes/devis/:id',
        element: (
          <ProtectedRoute permission="quotes:read">
            <LazyPage>
              <QuoteDetailPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'ventes/devis/:id/modifier',
        element: (
          <ProtectedRoute permission="quotes:write">
            <LazyPage>
              <QuoteFormPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'ventes/bons-livraison',
        element: (
          <ProtectedRoute permission="deliveries:read">
            <LazyPage>
              <DeliveryNotesPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'ventes/bons-livraison/nouveau',
        element: (
          <ProtectedRoute permission="deliveries:write">
            <LazyPage>
              <DeliveryNoteFormPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'ventes/recurrentes',
        element: (
          <ProtectedRoute permission="invoices:read">
            <LazyPage>
              <RecurringInvoicesPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'ventes/relances',
        element: (
          <ProtectedRoute permission="invoices:read">
            <LazyPage>
              <RemindersSettingsPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },

      // ─────────────────────────────────────────────────────────
      // Déclarations & calendrier fiscal (P1)
      // ─────────────────────────────────────────────────────────
      {
        path: 'declarations',
        element: (
          <ProtectedRoute permission="declarations:read">
            <LazyPage>
              <DeclarationsPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'declarations/calendrier',
        element: (
          <ProtectedRoute permission="declarations:read">
            <LazyPage>
              <FiscalCalendarPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },

      // ─────────────────────────────────────────────────────────
      // Comptabilité simplifiée (SMT) — régime réel simplifié
      // ─────────────────────────────────────────────────────────
      {
        path: 'compta-simplifiee',
        element: (
          <ProtectedRoute permission="accounting:read">
            <LazyPage>
              <SmtPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },

      // ─────────────────────────────────────────────────────────
      // Achats — fiches d'importation (landed cost). Le détail sert
      // aussi la création (:id = « nouveau »).
      // ─────────────────────────────────────────────────────────
      {
        path: 'achats/importations',
        element: (
          <ProtectedRoute permission="purchases:read">
            <LazyPage>
              <ImportCostSheetsPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'achats/importations/:id',
        element: (
          <ProtectedRoute permission="purchases:read">
            <LazyPage>
              <ImportCostSheetDetailPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },

      // ─────────────────────────────────────────────────────────
      // Analyses / BI dirigeant (P2) — transverse (dashboard:view)
      // ─────────────────────────────────────────────────────────
      {
        path: 'analyses',
        element: <Navigate to="/rapports/analyses" replace />,
      },

      // ─────────────────────────────────────────────────────────
      // Portail cabinet comptable (P1.4) — rôle CABINET_COMPTABLE
      // (cabinet:access) ; la page gère l'appartenance au cabinet.
      // ─────────────────────────────────────────────────────────
      {
        path: 'cabinet',
        element: (
          <ProtectedRoute permission="cabinet:access">
            <LazyPage>
              <FirmPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'cabinet/dossiers',
        element: (
          <ProtectedRoute permission="cabinet:access">
            <LazyPage>
              <FirmDashboardPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },

      // The company's accounting-firm link lives in the settings rail.
      {
        path: 'parametres/cabinet',
        element: <Navigate to="/settings/cabinet" replace />,
      },

      // Paramètres — webhooks sortants (P2, offre SUITE)
      {
        path: 'parametres/webhooks',
        element: (
          <ProtectedRoute permission="webhooks:read">
            <LazyPage>
              <WebhooksPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },

      // Audit
      {
        path: 'audit',
        element: (
          <ProtectedRoute permission="audit:view">
            <LazyPage>
              <AuditLogPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },

      // Settings shell — secondary navigation, one route per section (#36)
      {
        path: 'settings',
        element: (
          <ProtectedRoute anyPermission={[...SETTINGS_PERMISSIONS]}>
            <LazyPage>
              <SettingsLayout />
            </LazyPage>
          </ProtectedRoute>
        ),
        children: [
          { index: true, element: <LazyPage><SettingsIndexRedirect /></LazyPage> },
          {
            path: 'entreprise',
            element: (
              <ProtectedRoute permission="tenant:settings">
                <LazyPage>
                  <EntrepriseSection />
                </LazyPage>
              </ProtectedRoute>
            ),
          },
          {
            path: 'modules',
            element: (
              <ProtectedRoute permission="tenant:settings">
                <LazyPage>
                  <ModulesSection />
                </LazyPage>
              </ProtectedRoute>
            ),
          },
          {
            path: 'fiscalite',
            element: (
              <ProtectedRoute permission="tenant:settings">
                <LazyPage>
                  <FiscaliteSection />
                </LazyPage>
              </ProtectedRoute>
            ),
          },
          {
            path: 'facturation',
            element: (
              <ProtectedRoute permission="tenant:settings">
                <LazyPage>
                  <FacturationSection />
                </LazyPage>
              </ProtectedRoute>
            ),
          },
          {
            path: 'apparence',
            element: (
              <ProtectedRoute permission="tenant:settings">
                <LazyPage>
                  <ApparenceSection />
                </LazyPage>
              </ProtectedRoute>
            ),
          },
          {
            path: 'fidelite',
            element: (
              <ProtectedRoute permission="tenant:settings">
                <LazyPage>
                  <FideliteSection />
                </LazyPage>
              </ProtectedRoute>
            ),
          },
          {
            path: 'caisse',
            element: (
              <ProtectedRoute permission="tenant:settings">
                <LazyPage>
                  <CaisseSection />
                </LazyPage>
              </ProtectedRoute>
            ),
          },
          {
            path: 'modules-menus',
            element: (
              <ProtectedRoute permission="tenant:settings">
                <LazyPage>
                  <ModulesMenusSection />
                </LazyPage>
              </ProtectedRoute>
            ),
          },
          {
            path: 'tableau-de-bord',
            element: (
              <ProtectedRoute permission="tenant:settings">
                <LazyPage>
                  <TableauDeBordSection />
                </LazyPage>
              </ProtectedRoute>
            ),
          },
          {
            path: 'comptes-bancaires',
            element: (
              <ProtectedRoute permission="tenant:settings">
                <LazyPage>
                  <ComptesBancairesSection />
                </LazyPage>
              </ProtectedRoute>
            ),
          },
          {
            path: 'parametrage-dgi',
            element: (
              <ProtectedRoute anyPermission={['tenant:settings', 'tenant:parameter-tables']}>
                <LazyPage>
                  <ParametrageDgiSection />
                </LazyPage>
              </ProtectedRoute>
            ),
          },
          {
            path: 'notifications',
            element: (
              <ProtectedRoute permission="tenant:settings">
                <LazyPage>
                  <NotificationsSection />
                </LazyPage>
              </ProtectedRoute>
            ),
          },
          {
            path: 'cabinet',
            element: (
              <ProtectedRoute permission="tenant:settings">
                <LazyPage>
                  <CabinetSettingsPage />
                </LazyPage>
              </ProtectedRoute>
            ),
          },
          {
            path: 'pispi',
            element: (
              <ProtectedRoute permission="pispi:configure">
                <LazyPage>
                  <PispiSection />
                </LazyPage>
              </ProtectedRoute>
            ),
          },
          {
            path: 'mise-en-service',
            element: (
              <ProtectedRoute permission="tenant:settings">
                <LazyPage>
                  <MiseEnServiceSection />
                </LazyPage>
              </ProtectedRoute>
            ),
          },
          {
            path: 'agents-mcf',
            element: (
              <ProtectedRoute permission="mcf-agent:enroll">
                <LazyPage>
                  <McfAgentsSection />
                </LazyPage>
              </ProtectedRoute>
            ),
          },
          {
            path: 'appareils-mcf',
            element: (
              <ProtectedRoute permission="mcf:gerer-appareils">
                <LazyPage>
                  <McfDevicesSection />
                </LazyPage>
              </ProtectedRoute>
            ),
          },
          {
            path: 'pannes-mcf',
            element: (
              <ProtectedRoute permission="mcf:declarer-panne">
                <LazyPage>
                  <McfOutagesSection />
                </LazyPage>
              </ProtectedRoute>
            ),
          },
          {
            path: 'workflow',
            element: (
              <ProtectedRoute permission="tenant:workflow-config">
                <LazyPage>
                  <WorkflowConfigPage />
                </LazyPage>
              </ProtectedRoute>
            ),
          },
          {
            path: 'assistant-commandes',
            element: (
              <ProtectedRoute permission="tenant:settings">
                <LazyPage>
                  <AssistantCommandsSection />
                </LazyPage>
              </ProtectedRoute>
            ),
          },
        ],
      },

      // Admin - Users
      {
        path: 'admin/users',
        element: (
          <ProtectedRoute permission="users:read">
            <LazyPage>
              <UsersPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'admin/users/new',
        element: (
          <ProtectedRoute permission="users:create">
            <LazyPage>
              <UserFormPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'admin/users/:id',
        element: (
          <ProtectedRoute permission="users:update">
            <LazyPage>
              <UserFormPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },

      // Admin - ERP Connection
      {
        path: 'admin/erp-connection',
        element: (
          <ProtectedRoute permission="tenant:settings">
            <LazyPage>
              <ERPConnectionConfigPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },

      // Admin - Événements PI-SPI
      {
        path: 'admin/pispi-events',
        element: (
          <ProtectedRoute permission="pispi:configure">
            <LazyPage>
              <PispiEventsPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },

      // Admin - Node health (offline resilience)
      {
        path: 'admin/nodes',
        element: (
          <ProtectedRoute permission="tenant:settings">
            <LazyPage>
              <NodeHealthPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },

      // Admin - Établissements (Tâche 10, multi-établissement)
      {
        path: 'admin/establishments',
        element: (
          <ProtectedRoute permission="establishments:read">
            <LazyPage>
              <EstablishmentsPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },

      // Admin - Roles
      {
        path: 'admin/roles',
        element: (
          <ProtectedRoute permission="roles:read">
            <LazyPage>
              <RolesPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'admin/roles/new',
        element: (
          <ProtectedRoute permission="roles:create">
            <LazyPage>
              <RoleEditorPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'admin/roles/:id',
        element: (
          <ProtectedRoute permission="roles:update">
            <LazyPage>
              <RoleEditorPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },

      // Sector-module routes mount inside the protected layout, so a module
      // never escapes authentication; each route keeps its own permission
      // gate on top.
      ...MODULE_ROUTES,
    ],
  },

  // ─────────────────────────────────────────────────────────
  // Catch-all for 404
  // ─────────────────────────────────────────────────────────
  {
    path: '*',
    element: <NotFoundPage />,
    errorElement: <RouteErrorPage />,
  },
]);

export default router;
